import {
  Box,
  Button,
  Callout,
  Flex,
  Select,
  Spinner,
  Text,
} from "@radix-ui/themes";

import {
  DataTable,
  type DataTableColumns,
} from "~/v3-poc/components/DataTable";
import {
  ACTIVITIES_TYPE_FILTER_OPTIONS,
  type ActivitiesTypeFilter,
  type ActivityRow,
} from "./activities.utils";

export interface ActivitiesWidgetPresentationProps {
  rows: ActivityRow[];
  /** Effective workspace symbol — group symbol when grouped, else undefined. */
  symbol?: string;
  scopeNotice?: string;
  typeFilter: ActivitiesTypeFilter;
  onTypeFilterChange: (value: ActivitiesTypeFilter) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
  isAccountReady: boolean;
  pageNumber: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

const COLUMNS: DataTableColumns<ActivityRow> = [
  {
    id: "description",
    header: "Description",
    accessorKey: "descriptionLabel",
    cell: ({ row }) => (
      <Text size="1" weight="medium">
        {row.original.descriptionLabel}
      </Text>
    ),
  },
  {
    id: "activity_type",
    header: "Type",
    accessorKey: "typeLabel",
    cell: ({ row }) => <Text size="1">{row.original.typeLabel}</Text>,
  },
  {
    id: "qty",
    header: "Qty",
    accessorKey: "quantityLabel",
    meta: { align: "end", numeric: true },
    cell: ({ row }) => <Text size="1">{row.original.quantityLabel}</Text>,
  },
  {
    id: "amount",
    header: "Amount",
    accessorKey: "amountLabel",
    meta: { align: "end", numeric: true },
    cell: ({ row }) => <Text size="1">{row.original.amountLabel}</Text>,
  },
  {
    id: "date",
    header: "Date",
    accessorKey: "dateLabel",
    meta: { align: "end", numeric: true },
    cell: ({ row }) => <Text size="1">{row.original.dateLabel}</Text>,
  },
];

const ActivitiesError = ({
  errorMessage,
  isFetching,
  onRetry,
}: {
  errorMessage?: string;
  isFetching: boolean;
  onRetry: () => void;
}) => (
  <Callout.Root color="red" role="alert">
    <Callout.Text>
      {errorMessage ?? "Activities could not be loaded."}
    </Callout.Text>
    <Button
      type="button"
      size="1"
      variant="soft"
      color="red"
      onClick={onRetry}
      loading={isFetching}
    >
      Retry
    </Button>
  </Callout.Root>
);

export const ActivitiesWidgetPresentation = ({
  rows,
  symbol,
  scopeNotice,
  typeFilter,
  onTypeFilterChange,
  isLoading,
  isFetching,
  isError,
  errorMessage,
  onRetry,
  isAccountReady,
  pageNumber,
  hasPreviousPage,
  hasNextPage,
  onPreviousPage,
  onNextPage,
}: ActivitiesWidgetPresentationProps) => {
  const emptyMessage = !isAccountReady
    ? "Select an account to view activities."
    : symbol
      ? `No activities for ${symbol} on this page.`
      : "No activities to show.";

  const showPagination =
    isAccountReady && !isLoading && (hasPreviousPage || hasNextPage);

  return (
    <Flex direction="column" height="100%" minHeight="0">
      <Flex
        align="center"
        justify="between"
        gap="2"
        px="2"
        py="1"
        flexShrink="0"
        wrap="wrap"
        className="border-[color:var(--gray-a5)] border-b"
      >
        <Flex direction="column" gap="1">
          <Text size="1" color="gray">
            {symbol ? `Activities · ${symbol}` : "All activities"}
          </Text>
          {scopeNotice ? (
            <Text size="1" color="orange">
              {scopeNotice}
            </Text>
          ) : null}
        </Flex>
        <Flex align="center" gap="2">
          <Text size="1" color="gray">
            Type
          </Text>
          <Select.Root
            size="1"
            value={typeFilter}
            onValueChange={(value) => {
              const selectedFilter = ACTIVITIES_TYPE_FILTER_OPTIONS.find(
                (option) => option.value === value,
              )?.value;
              if (selectedFilter) {
                onTypeFilterChange(selectedFilter);
              }
            }}
          >
            <Select.Trigger aria-label="Activity type" />
            <Select.Content>
              {ACTIVITIES_TYPE_FILTER_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Flex>

      {isError && rows.length > 0 ? (
        <Box p="2" flexShrink="0">
          <ActivitiesError
            errorMessage={errorMessage}
            isFetching={isFetching}
            onRetry={onRetry}
          />
        </Box>
      ) : null}

      <Box flexGrow="1" minHeight="0" overflow="auto" position="relative">
        {isLoading && rows.length === 0 ? (
          <Flex align="center" justify="center" height="100%" p="4">
            <Spinner size="2" />
          </Flex>
        ) : isError && rows.length === 0 ? (
          <Box p="2">
            <ActivitiesError
              errorMessage={errorMessage}
              isFetching={isFetching}
              onRetry={onRetry}
            />
          </Box>
        ) : (
          <DataTable
            columns={COLUMNS}
            data={rows}
            variant="ghost"
            size="1"
            caption="Account activities"
            emptyMessage={emptyMessage}
          />
        )}
      </Box>

      {showPagination ? (
        <Flex
          align="center"
          justify="between"
          gap="2"
          px="2"
          py="1"
          flexShrink="0"
          wrap="wrap"
          className="border-[color:var(--gray-a5)] border-t"
          data-testid="activities-pagination"
        >
          <Text size="1" color="gray" className="atd-numeric">
            Page {pageNumber} · {rows.length} {symbol ? "matching " : ""}
            {rows.length === 1 ? "activity" : "activities"}
          </Text>
          <Flex align="center" gap="2">
            <Button
              type="button"
              size="1"
              variant="soft"
              color="gray"
              onClick={onPreviousPage}
              disabled={!hasPreviousPage || isFetching}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="1"
              variant="soft"
              color="gray"
              onClick={onNextPage}
              disabled={!hasNextPage || isFetching}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
};
